import React, { useState, useEffect, useMemo } from "react";
import { Trans, t } from "@lingui/macro";
import PropTypes from "kf-proptypes/widgets";

import { i18n } from "base/i18n";
import { useTheme } from "base/themes";

import { Show } from "@widgets/common/control_flow/index.jsx";
import {
  systemColors,
  semanticColors,
  primarySecondaryColor
} from "./constants.js";
import {
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { toColorVariable } from "./utils.js";
import { ColorItem, COLOR_ITEM_SIZES } from "./item.jsx";
import { CustomColorPicker } from "./custom.color.jsx";

import styles from "./style.css";

const NEW_CUSTOM_COLOR_STATE = {
  Id: "",
  Color: "#ff0000",
  Name: "",
  Type: "Solid",
  IsDark: false,
  IsCustom: true
};

const CUSTOM_COLOR_LIMIT = 24;

const DEFAULT_CONFIG = {
  primarySecondaryColors: true,
  systemColors: true,
  semanticColors: true,
  editCustomColors: true,
  showCustomColorNameInput: true
};

export function ColorPalette({
  style,
  value,
  onChange,
  showCustomColors = false,
  customColorsLimit = CUSTOM_COLOR_LIMIT,
  customColors = [],
  onSave,
  colorText,
  config = DEFAULT_CONFIG,
  userColors = []
}) {
  const [mode, setMode] = useState("palette");
  const [selectedCustomColor, setSelectedCustomColor] = useState({});

  useEffect(
    function onload() {
      let selectedColor = customColors.find((color) => color.ref === value);
      if (selectedColor) {
        setSelectedCustomColor(selectedColor);
      }
    },
    [value, customColors]
  );

  const userConfig = useMemo(
    function getConfig() {
      return {
        ...DEFAULT_CONFIG,
        ...(config || {})
      };
    },
    [config]
  );

  function onCustomColorSelect(value, colorInfo) {
    setSelectedCustomColor(colorInfo);
    onChange(value);
  }

  function onSystemColorSelect(value) {
    setSelectedCustomColor({});
    onChange(value);
  }

  return (
    <>
      {mode === "palette" ? (
        <div className={styles.palette} style={style}>
          <div className={styles.palettetitle}>
            <span className={`tiny`}>
              <Trans>Pick color</Trans>
            </span>
          </div>
          <div className={styles.palettecontent}>
            <div className={styles.selectedContent}>
              <div>
                <ColorItem
                  color={toColorVariable(value)}
                  size={COLOR_ITEM_SIZES.SMALL}
                />
                <span className={`micro`}>{colorText}</span>
              </div>
              {/* Show edit only if selected color is custom color */}
              {selectedCustomColor.ref && userConfig.editCustomColors ? (
                <span
                  className={`micro ${styles.clickables}`}
                  onClick={() => setMode("picker_edit")}
                >
                  <Trans>Edit</Trans>
                </span>
              ) : null}
            </div>

            <div className={styles.paletteSectionContainer}>
              {showCustomColors ? (
                <div className={styles.palettesection}>
                  <div className={`micro ${styles.sectiontitle}`}>
                    <Trans>Custom colors</Trans>
                  </div>
                  <div className={styles.colors}>
                    {customColors.map((color) => {
                      return (
                        <ColorItem
                          color={toColorVariable(color.ref)}
                          label={color.Name}
                          selected={value === color.ref}
                          onChange={() => onCustomColorSelect(color.ref, color)}
                          key={color.Id}
                        />
                      );
                    })}
                    {customColors.length < customColorsLimit ? (
                      <KfIcon
                        icon={KF_ICONS.PLUS_3}
                        size={ICON_SIZE.SIZE_16}
                        stroke={ICON_STROKE.THIN}
                        color={"--kf-color-primary-500"}
                        className={styles.clickables}
                        onClick={() => setMode("picker_add")}
                      />
                    ) : null}
                  </div>
                </div>
              ) : null}

              <Show when={userConfig.systemColors}>
                <ColorPaletteList
                  label={t`System colors`}
                  list={systemColors}
                  onChange={onSystemColorSelect}
                  value={value}
                />
              </Show>

              <Show when={userConfig.semanticColors}>
                <ColorPaletteList
                  label={t`Semantic colors`}
                  list={semanticColors}
                  onChange={onSystemColorSelect}
                  value={value}
                />
              </Show>

              <Show when={userConfig.primarySecondaryColors}>
                <ColorPaletteList
                  label={t`Primary & secondary colors`}
                  list={primarySecondaryColor}
                  onChange={onSystemColorSelect}
                  value={value}
                />
              </Show>
              {userColors.map(({ label, list }) => (
                <ColorPaletteList
                  key={label}
                  label={i18n._(label)}
                  list={list}
                  onChange={onSystemColorSelect}
                  value={value}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {mode.startsWith("picker") ? (
        <CustomColorPicker
          showColorNameInput={userConfig.showCustomColorNameInput}
          customColors={customColors}
          selectedColor={
            mode === "picker_add" ? NEW_CUSTOM_COLOR_STATE : selectedCustomColor
          }
          onSave={(colorInfo, mode) => {
            onSave(colorInfo, mode);
            setMode("palette");
          }}
          onCancel={() => setMode("palette")}
        />
      ) : null}
    </>
  );
}

ColorPalette.propTypes = {
  /**
   * onChange is triggered when user selects any color from palette
   */
  onChange: PropTypes.func,
  /**
   * onSave is triggered when user adds/edits a custom color
   */
  onSave: PropTypes.func,
  /**
   * select a dot notation color value for color palette
   */
  value: PropTypes.string,
  /**
   * enable to add & edit custom colors
   */
  showCustomColors: PropTypes.bool,
  /**
   * List of custom colors
   */
  customColors: PropTypes.arrayOf(
    PropTypes.shape({
      ref: PropTypes.string,
      Id: PropTypes.string,
      Color: PropTypes.string,
      Name: PropTypes.string,
      Type: PropTypes.string,
      IsDark: PropTypes.bool,
      IsCustom: PropTypes.bool
    })
  ),
  /**
   * Generated style object for given variable, (applicable if any of variable is changed from theme)
   */
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  /**
   * limit for adding custom colors.
   */ customColorsLimit: PropTypes.number,
  /**
   * Color name to be shown in top of the picker
   */
  colorText: PropTypes.string,

  /**
   * object to show or hide default app builder controllers
   */
  config: PropTypes.shape({
    primarySecondaryColors: PropTypes.bool,
    semanticColors: PropTypes.bool,
    systemColors: PropTypes.bool,
    showCustomColorNameInput: PropTypes.bool,
    editCustomColors: PropTypes.bool
  }),
  /**
   * List to add user defined colors
   */
  userColors: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      list: PropTypes.arrayOf(PropTypes.string)
    })
  )
};

function ColorPaletteList({ label, list, value, onChange }) {
  let { themeClass } = useTheme();
  return (
    <div className={styles.palettesection}>
      <div className={`micro-400 ${styles.sectiontitle}`}>{label}</div>
      <div className={`${styles.colors} ${themeClass}`}>
        {list.map((color) => {
          return (
            <ColorItem
              color={toColorVariable(`${color}`)}
              label={color}
              selected={value === color}
              onChange={() => onChange(color)}
              key={color}
            />
          );
        })}
      </div>
    </div>
  );
}

ColorPaletteList.propTypes = {
  label: PropTypes.string,
  list: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  value: PropTypes.string,
  itemPrefix: PropTypes.string
};
